import { memo } from 'react';

import { type AssetCustomFieldValue, type BoardCustomFieldValue } from '@air/api/types';
import { EditableDate, type EditableDateProps } from '@air/component-editable-date';

import { CUSTOM_FIELD_DATE } from '~/constants/testIDs';

export type CustomFieldDateInputProps = {
  field: BoardCustomFieldValue | AssetCustomFieldValue;
  onBlur?: () => void;
  onChange: (date: Date | null) => void;
  canEditCustomFields: boolean;
} & Omit<EditableDateProps, 'label' | 'name' | 'dateValue' | 'variant' | 'onEditingStateChange' | 'onChange'>;

export const CustomFieldDateInput = memo(
  ({ field, onBlur, onChange, canEditCustomFields, ...rest }: CustomFieldDateInputProps) => {
    return (
      <EditableDate
        {...rest}
        // biome-ignore lint/performance/noJsxPropsBind: this date change handler compares against this field's current dateValue
        onChange={(date: Date | null) => {
          const dateValue = date?.toISOString();

          if (dateValue === field.dateValue) {
            return;
          }

          onChange(date);
        }}
        // biome-ignore lint/performance/noJsxPropsBind: blur is forwarded only when editing ends for this field
        onEditingStateChange={(isEditing) => {
          if (!isEditing) {
            onBlur?.();
          }
        }}
        // biome-ignore lint/plugin: Cypress selects this date input by the field name baked into the test id
        data-testid={`${CUSTOM_FIELD_DATE}-${field.name}`}
        size="large"
        name={field.id}
        dateValue={field.dateValue}
        placeholder={canEditCustomFields ? 'Add a date' : 'None'}
        readOnly={!canEditCustomFields}
        showCalendarIcon={false}
        buttonClassName="rounded-4 bg-transparent p-1.5 transition-colors border ads:border-input border-gray-5 ads:hover:border-hover-input hover:border-gray-7 ads:hover:bg-hover-subtle hover:bg-gray-2 pl-2"
      />
    );
  },
);

CustomFieldDateInput.displayName = 'CustomFieldDateInput';
